import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { ESCALATION_LABELS, SLA_COLORS, STAGE_COLORS, fmtMoney } from "@/lib/constants";
import {
  ACTIONS, APPROVAL_LABELS, CheckIcon, EventList, Panel,
  approvalTone, disabledReason, enabled, kycTone,
} from "@/components/client/ClientPanels";

export default function ClientProfile() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const run = async (action) => {
    setBusy(action.key);
    try {
      const { data } = await api.post(`/clients/${id}/${action.key}`);
      setClient(data);
      toast.success(`${action.label} — done`, {
        description: action.key === "write-back" ? data.workflow.outcome : `Client ${data.id} updated. Audit log recorded.`,
      });
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy("");
    }
  };

  if (!client) {
    return <div className="font-mono text-zinc-500 text-sm animate-pulse" data-testid="profile-loading">LOADING CLIENT PROFILE…</div>;
  }

  const wf = client.workflow;

  const stats = [
    { label: "AI Priority Score", value: client.score, cls: client.score >= 80 ? "text-red-400" : "text-emerald-400" },
    { label: "Est. Commercial Value", value: fmtMoney(client.value), cls: "text-zinc-100" },
    { label: "Stuck At", value: null, text: client.stuck_at },
    { label: "Reason Detected", value: null, text: client.reason },
  ];

  return (
    <div className="space-y-6" data-testid="client-profile-page">
      <div>
        <Link to="/queue" data-testid="back-to-queue-link" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-emerald-400 transition-colors duration-150 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to queue
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">{client.name}</h1>
          <span className="font-mono text-sm text-zinc-500" data-testid="profile-client-id">{client.id}</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${STAGE_COLORS[client.stage] || ""}`} data-testid="profile-stage-badge">{client.stage}</span>
          <span className={`text-xs font-semibold ${SLA_COLORS[client.sla.status] || ""}`} data-testid="profile-sla-badge">{client.sla.label}</span>
          {client.escalation !== "none" && (
            <span className="text-xs font-semibold text-red-400 border border-red-500/40 bg-red-500/10 rounded-full px-2.5 py-0.5" data-testid="profile-escalation-badge">
              Escalated · {ESCALATION_LABELS[client.escalation]}
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-500 mt-2">
          {client.country} · {client.language} · {client.jurisdiction} · Assigned to {client.assignee} · Preferred channel {client.channel}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stats.map((c, i) => (
          <div key={c.label} className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid={`profile-stat-${i}`}>
            <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">{c.label}</div>
            {c.value !== null
              ? <div className={`text-2xl font-bold font-mono ${c.cls}`}>{c.value}</div>
              : <div className="text-xs text-zinc-300 leading-relaxed">{c.text}</div>}
          </div>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="action-toolbar">
        <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-3">Workflow Actions — every action mutates state and writes to the audit log</div>
        <div className="flex flex-wrap gap-2">
          {ACTIONS.map((a) => {
            const ok = enabled(a.key, client);
            return (
              <button
                key={a.key}
                data-testid={`btn-${a.key}`}
                disabled={!ok || busy !== ""}
                title={ok ? a.label : disabledReason(a.key, client)}
                onClick={() => run(a)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-sm text-xs font-semibold border transition-colors duration-200 ${
                  ok
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/20"
                    : "bg-zinc-900 text-zinc-600 border-zinc-800 cursor-not-allowed"
                }`}
              >
                {busy === a.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <a.icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {a.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-zinc-900 border border-emerald-500/30 rounded-md p-6 shadow-[0_0_15px_rgba(16,185,129,0.08)]" data-testid="ai-decision-panel">
        <h3 className="text-base font-semibold text-emerald-400 mb-5">O.N.E.Tech AI — Decision Pipeline</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">AI Interpretation</div>
              <p className="text-xs text-zinc-300 leading-relaxed" data-testid="ai-interpretation">{client.interpretation}</p>
            </div>
            <div>
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Latest Signal</div>
              {wf.signal ? (
                <div className="text-xs text-zinc-300" data-testid="latest-signal">
                  {wf.signal.text}
                  <span className="block text-[10px] font-mono text-zinc-600 mt-1">{wf.signal.source}</span>
                </div>
              ) : <div className="text-xs text-zinc-600">No new signal — use “Simulate Incoming Signal”.</div>}
            </div>
            <div>
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Next Best Action</div>
              {wf.nba ? (
                <div data-testid="nba-recommendation">
                  <p className="text-xs text-zinc-200 leading-relaxed">{wf.nba.action}</p>
                  <p className="text-[11px] font-mono text-emerald-500 mt-1.5">Channel: {wf.nba.channel}{wf.nba.language ? ` · Language: ${wf.nba.language}` : ""}</p>
                  {wf.nba.timing && <p className="text-[11px] font-mono text-zinc-500 mt-0.5">{wf.nba.timing}</p>}
                  <p className="text-[11px] text-zinc-500 mt-2 italic border-l-2 border-zinc-700 pl-3">{wf.nba.message}</p>
                </div>
              ) : <div className="text-xs text-zinc-600">Not generated yet.</div>}
            </div>
          </div>
          <div className="space-y-5">
            <div>
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Governance Decision</div>
              {wf.governance ? (
                <div data-testid="governance-decision">
                  <div className="text-xs font-semibold text-zinc-200 mb-2">{wf.governance.decision}</div>
                  <div className="space-y-1.5">
                    {wf.governance.checks.map((chk) => (
                      <div key={chk.check} className="flex items-start gap-2 text-[11px]">
                        <CheckIcon status={chk.passed ? "passed" : "rejected"} />
                        <span className="text-zinc-400">{chk.check}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div className="text-xs text-zinc-600">Governance evaluation runs with action generation.</div>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Human Approval</div>
                <div className={`text-xs font-semibold ${approvalTone(wf.approval)}`} data-testid="approval-status">
                  {APPROVAL_LABELS[wf.approval]}
                  {wf.approval === "pending_human" && wf.approval_route ? ` — ${wf.approval_route}` : ""}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Execution Status</div>
                <div className={`text-xs font-semibold ${wf.execution === "executed" ? "text-emerald-400" : "text-zinc-400"}`} data-testid="execution-status">
                  {wf.execution === "executed" ? "Executed (simulated delivery)" : "Not executed"}
                </div>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Outcome &amp; CRM Write-Back</div>
              {wf.outcome ? (
                <div data-testid="outcome-writeback">
                  <div className="text-xs text-zinc-200">{wf.outcome}</div>
                  <div className="text-[11px] font-mono text-emerald-500 mt-1">{wf.crm_writeback ? "WRITTEN BACK TO CRM (simulated)" : "Pending write-back"}</div>
                </div>
              ) : <div className="text-xs text-zinc-600">No outcome recorded yet.</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="CRM Events" testId="panel-crm-events"><EventList events={client.crm_events} empty="No CRM events." /></Panel>
        <Panel title="KYC Status" testId="panel-kyc">
          <div className="flex items-center justify-between mb-3">
            <span className={`text-xs font-semibold ${kycTone(client.kyc.status)}`} data-testid="kyc-status">
              {client.kyc.status}
            </span>
            <span className="text-[10px] font-mono text-zinc-600">{client.kyc.provider}</span>
          </div>
          <div className="space-y-2.5">
            {client.kyc.checks.length === 0 && <div className="text-xs text-zinc-600">No checks submitted yet.</div>}
            {client.kyc.checks.map((chk) => (
              <div key={chk.name} className="flex items-start gap-2 text-xs">
                <CheckIcon status={chk.status} />
                <div>
                  <div className="text-zinc-300">{chk.name}</div>
                  <div className="text-zinc-600 text-[11px]">{chk.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Payment Events" testId="panel-payments"><EventList events={client.payments} empty="No payment events." /></Panel>
        <Panel title="MT5 Trading Activity" testId="panel-mt5"><EventList events={client.trading} empty="No trading activity." /></Panel>
        <Panel title="Communication History" testId="panel-communications"><EventList events={client.communications} empty="No communications." /></Panel>
        <Panel title="Activity Timeline" testId="panel-timeline"><EventList events={client.timeline} empty="No workflow activity yet." /></Panel>
      </div>
    </div>
  );
}
